import { swSettings } from "app/common/services/swSettings";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { KeywordsGroupUtilities } from "app/UtilitiesAndConstants/UtilityFunctions/KeywordsGroupUtilities";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";

import { i18nFilter } from "../../filters/i18nFilter";
import RegexPatterns from "../../services/RegexPatterns";

interface IKeywordsGroupEditorGroup {
    title: string;
    items: { text: string }[];
    Id: string;
    GroupHash: string;
    LastUpdated: string;
    UniqueId?: string;
}

export const emptyKeywordGroup: IUserKeywordGroup = {
    AddedTime: undefined,
    GroupHash: "",
    Id: "",
    Keywords: [],
    LastUpdated: undefined,
    Name: "",
    UserId: undefined,
};

export interface ITitleValidation {
    isValid: boolean;
    errorMessage?: string;
}

export const TITLE_MAX_LENGTH = 26;

export const KeywordGroupEditorHelpers = {
    validateTitle: async (
        newGroupName: string,
        keywordsGroup: IKeywordsGroupEditorGroup | null = null,
    ): Promise<ITitleValidation> => {
        if (!newGroupName || newGroupName.length === 0) {
            return {
                isValid: false,
                errorMessage: i18nFilter()("KeywordAnalysis.keywordgroup.wizard.required"),
            };
        }
        if (newGroupName.length > TITLE_MAX_LENGTH) {
            return {
                isValid: false,
                errorMessage: i18nFilter()("KeywordAnalysis.keywordgroup.wizard.title.max.length"),
            };
        }
        const isFormatValid = RegexPatterns.unicodeOrWhitespaceOnly(newGroupName);
        if (!isFormatValid) {
            return {
                isValid: false,
                errorMessage: "Invalid characters",
            };
        }

        const groupWithThisName = await KeywordsGroupUtilities.getUserGroupByName(newGroupName);
        const { Name, Id } = groupWithThisName || {};
        const nameExists = {
            isValid: false,
            errorMessage: `'${Name}' is already used`,
        };
        // Better comparison by ID
        if (Id) {
            if (groupWithThisName?.Id !== keywordsGroup?.Id) {
                return nameExists;
            }
        } else {
            // Legacy support
            // @ts-expect-error This comparison appears to be unintentional because the types 'IUserGroup' and 'IKeywordsGroupEditorGroup' have no overlap
            if (Name && groupWithThisName !== keywordsGroup) {
                return nameExists;
            }
        }

        return {
            isValid: true,
            errorMessage: ``,
        };
    },
    keywordGroupFromList: (target: any = {}, existedGroup = emptyKeywordGroup): IUserKeywordGroup => {
        const { title = null, items = [] } = target;
        const { GroupHash, Id } = existedGroup;
        return {
            Name: title,
            Keywords: items.map(({ text }) => text.toLowerCase()),
            GroupHash,
            Id,
        };
    },
    toEditorGroup: (existedGroup: IUserKeywordGroup = emptyKeywordGroup): IKeywordsGroupEditorGroup => {
        const { Name, Keywords, Id, GroupHash, LastUpdated } = existedGroup;
        return {
            title: Name,
            items: Keywords.map((text) => ({ text: text.toLowerCase() })),
            Id,
            GroupHash,
            LastUpdated: LastUpdated || "",
        };
    },
    getMaxGroupCount: () => {
        return swSettings.components.KeywordAnalysis.resources.maxGroupCount;
    },
};
